import { apiClient } from './client'
import type { ClientConfig } from '@/types/domain'

const http = () => apiClient.axios

/**
 * The deployment's client-facing limits and flags — `GET /v1/config`.
 *
 * An endpoint of its own rather than more fields on `/v1/auth/me`: that
 * response *is* the user, and the same object rides on every login and
 * refresh, so policy put there goes stale in every client that caches the
 * session. This one may be re-read whenever a screen wants the current
 * answer.
 *
 * It replaces the client's own `DEFAULT_MAX_UPLOAD_MB = 50`: a constant
 * duplicating a server setting is right until the day the server changes,
 * and then it is wrong in the one place a person sees it (an upload the
 * browser accepts and the gateway refuses).
 */
export const configApi = {
  async get(): Promise<ClientConfig> {
    const { data } = await http().get<Partial<ClientConfig>>('/v1/config')
    return normalizeConfig(data)
  },
}

/**
 * Fills in what an older gateway (or a failed partial answer) leaves out.
 *
 * The defaults are the *safe* readings rather than the generous ones: both
 * gates off. A missing `verification` block must not be read as "verification
 * is on and this address is unconfirmed" — that would put a warning banner
 * in front of people on a deployment that never verifies anything.
 */
export function normalizeConfig(raw: Partial<ClientConfig> | null | undefined): ClientConfig {
  return {
    upload: {
      maxSizeMb: raw?.upload?.maxSizeMb ?? DEFAULT_MAX_UPLOAD_MB,
      accepts: raw?.upload?.accepts ?? [...DEFAULT_UPLOAD_ACCEPTS],
    },
    verification: {
      enabled: raw?.verification?.enabled ?? false,
      ...(raw?.verification?.graceHours != null
        ? { graceHours: raw.verification.graceHours }
        : {}),
      ...(raw?.verification?.emailVerified != null
        ? { emailVerified: raw.verification.emailVerified }
        : {}),
      ...(raw?.verification?.uploadsPausedAt
        ? { uploadsPausedAt: raw.verification.uploadsPausedAt }
        : {}),
    },
    billing: { enabled: raw?.billing?.enabled ?? false },
    // Safe reading again: an older gateway that says nothing about a model
    // must not have one assumed for it, or a screen would name a vendor that
    // never reads anything.
    ai: {
      available: raw?.ai?.available ?? false,
      ...(raw?.ai?.provider ? { provider: raw.ai.provider } : {}),
      ...(raw?.ai?.region ? { region: raw.ai.region } : {}),
    },
  }
}

/**
 * The gateway's own fallback (`defaultUploadMaxSizeMB`), used only when the
 * config request has not answered yet or answered without the key. Screens
 * read `configApi.get().upload.maxSizeMb`, not this.
 */
export const DEFAULT_MAX_UPLOAD_MB = 50

export const DEFAULT_UPLOAD_ACCEPTS = ['image/jpeg', 'image/png', 'application/pdf'] as const

/** Whether the verification banner has anything true to say. False whenever
 *  the gate is off, so no screen infers one from `emailVerified` alone. */
export function showsVerificationNotice(config: ClientConfig): boolean {
  return config.verification.enabled && config.verification.emailVerified === false
}
